import { useMemo } from 'react';

import { EmptyImage } from 'app/components/commons/Images/EmptyImage/EmptyImage';
import { SingleImage } from 'app/components/commons/Images/SingleImage/SingleImage';
import { pictureToPictureView } from 'app/components/pages/Experiences/Common/_components/Medias/_components/utils';
import { useEmojis } from 'app/hooks/data/useEmojis';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { Picture } from 'app/typings';
import { EXPERIENCE_STATUS } from 'app/typings/core/admin/experiences/enum/experienceStatus';
import type { ExperienceCategory } from 'app/typings/experiences';

import type { HotelActivity } from '../_types';

import {
  formatExperienceName,
  getActivityPath,
  groupActivitiesByType,
} from './_utils/activityUtils';
import { EntityRow } from './entity/EntityRow';
import { EntitySection } from './entity/EntitySection';
import { EntityTable } from './entity/EntityTable';
import type { EntityRowConfig, EntitySectionConfig } from './entity/entity';
import { SortableSection } from './sortable/SortableSection';

type Props = {
  hotelId: number;
  activities: HotelActivity[];
  onCreateActivity: () => void;
  onPublish: (activity: HotelActivity) => void;
  onUnpublish: (activity: HotelActivity) => void;
  onReorder?: (sectionKey: string, items: HotelActivity[]) => void;
  isLegacy: boolean;
};

const COVER_IMAGE_SIZE = 40;

const getRowConfig = (
  activity: HotelActivity,
  handlers: Pick<Props, 'onPublish' | 'onUnpublish'>,
  isLegacy: boolean,
  experienceCategories: ExperienceCategory[] | undefined,
  emojis?: Picture[]
): EntityRowConfig => {
  const category = experienceCategories?.find(
    (cat) => cat.id === activity.category.id
  );

  const emoji = emojis?.find((e) => e.id === category?.pictureId);

  const images = activity.coverPicture
    ? activity.coverPicture
    : activity.emoji
    ? activity.emoji
    : emoji
    ? pictureToPictureView(emoji)
    : undefined;

  return {
    id: activity.id,
    cover: images ? (
      <SingleImage
        image={images}
        width={COVER_IMAGE_SIZE}
        height={COVER_IMAGE_SIZE}
      />
    ) : (
      <EmptyImage width={COVER_IMAGE_SIZE} height={COVER_IMAGE_SIZE} />
    ),
    primaryLabel: formatExperienceName(activity),
    status: activity.status,
    redirectPath: getActivityPath(activity, isLegacy),
    actions: [
      activity.status !== EXPERIENCE_STATUS.PUBLISHED && {
        key: 'publish',
        label: 'Publish',
        onClick: () => handlers.onPublish(activity),
      },
      activity.status !== EXPERIENCE_STATUS.UNPUBLISHED && {
        key: 'unpublish',
        label: 'Unpublish',
        onClick: () => handlers.onUnpublish(activity),
      },
    ].filter(Boolean) as EntityRowConfig['actions'],
  };
};

const getActivitySortLabel = (activity: HotelActivity): string =>
  formatExperienceName(activity) ||
  activity.description ||
  `Activity ${activity.id}`;

const getActivityStatusSortOrder = (activity: HotelActivity): number => {
  if (activity.status === EXPERIENCE_STATUS.PUBLISHED) {
    return 0;
  }

  if (activity.status === EXPERIENCE_STATUS.UNPUBLISHED) {
    return 1;
  }

  return 2;
};

const sortOtherActivities = (activities: HotelActivity[]) =>
  [...activities].sort((a, b) => {
    const statusOrder: number =
      getActivityStatusSortOrder(a) - getActivityStatusSortOrder(b);

    if (statusOrder !== 0) {
      return statusOrder;
    }

    return getActivitySortLabel(a).localeCompare(
      getActivitySortLabel(b),
      undefined,
      {
        sensitivity: 'base',
      }
    );
  });

export const ActivitiesTable = ({
  activities,
  onCreateActivity,
  onPublish,
  onUnpublish,
  onReorder,
  isLegacy,
}: Props) => {
  const { data: experienceCategories } = useExperiencesCategories();
  const { data: emojis } = useEmojis();

  const handlers = useMemo(
    () => ({ onPublish, onUnpublish }),
    [onPublish, onUnpublish]
  );

  const { alwaysIncluded, others } = useMemo(
    () => groupActivitiesByType(activities),
    [activities]
  );

  const sortedOthers = useMemo(() => sortOtherActivities(others), [others]);

  const hasAlwaysIncluded = alwaysIncluded.length > 0;
  const allActivitiesEmpty =
    alwaysIncluded.length === 0 && sortedOthers.length === 0;

  const sections: EntitySectionConfig<HotelActivity>[] = allActivitiesEmpty
    ? [{ key: 'all-activities', items: [] }]
    : [
        {
          key: 'always-included',
          label: hasAlwaysIncluded ? 'Always included overnight' : undefined,
          items: alwaysIncluded,
        },
        {
          key: 'others',
          label: hasAlwaysIncluded ? 'Others' : undefined,
          items: sortedOthers,
        },
      ].filter((section) => section.items.length > 0);

  const renderSection = (section: EntitySectionConfig<HotelActivity>) => {
    const rowConfigGetter = (activity: HotelActivity) =>
      getRowConfig(activity, handlers, isLegacy, experienceCategories, emojis);

    if (onReorder) {
      return (
        <SortableSection
          key={section.key}
          section={section}
          getId={(activity) => activity.id}
          renderItem={(activity, dragHandleProps) => (
            <EntityRow
              tableType="activity"
              config={rowConfigGetter(activity)}
              dragHandleProps={dragHandleProps}
              isDraggable={section.key === 'always-included'}
            />
          )}
          onReorder={onReorder}
          emptyLabel="No activities"
          emptyDescription="No activities have been created for this hotel yet."
          showEmptyState={allActivitiesEmpty}
        />
      );
    }

    return (
      <EntitySection
        key={section.key}
        section={section}
        renderItem={(activity) => (
          <EntityRow
            tableType="activity"
            key={activity.id}
            config={rowConfigGetter(activity)}
          />
        )}
        emptyLabel="No activities"
        emptyDescription="No activities have been created for this hotel yet."
        showEmptyState={allActivitiesEmpty}
      />
    );
  };

  return (
    <EntityTable
      title="Activities & amenities"
      createLabel="Create new activity"
      sections={sections}
      renderSection={renderSection}
      onCreate={onCreateActivity}
    />
  );
};
